import { AxiosResponse } from 'axios';
import { StorableBaseService, FormField, PaginatedResponse } from './_base';
import employeeGroupService from './employee-groups';

import { i18n } from '@/i18n/i18n';

// Import deviceService lazily to avoid circular dependency
import { DeviceService } from './devices';
import projectCategoryService, { ProjectCategory } from './project-categories';
const getDeviceService = () => import('./devices').then(m => m.default) as Promise<DeviceService>;

const $t = i18n.global.t;

interface ProjectListParams {
    ids?: Array<string>;
    includeArchived?: boolean;
    recent?: boolean;
    page?: number;
    pagesize?: number;
    query?: string;
    forEmployee?: string;
    category_ids?: Array<string>;
}

interface ProjectLocation {
    longitude: string;
    latitude: string;
    max_distance: number;
    address?: string;
}

interface IntervalBasedRule {
    reference: 'workday' | 'recorded_section';
    min_working_time?: number;
    max_working_time?: number;
}

interface Project {
    id: string;
    archived_at?: string;
    locations: Array<ProjectLocation>;
    group_assignments: Array<string>;
    device_assignments: Array<string>;
    timebased_rules: Array<Array<Array<string>>>;
    interval_based_rules: Array<IntervalBasedRule>;
    name: string;
    code: string;
    category_id?: string;
    category_name?: string;
    color: string;
    value_type: 'relative' | 'absolute';
    value: string;
    value_employee_configurable: boolean;
}

interface TagAssignment {
    tag_id: string;
    type: 'relative' | 'absolute';
    value: string;
}

export const TYPE_CHOICES = [
    {
        id: 'relative',
        $display: $t('message.prorated'),
    },
    {
        id: 'absolute',
        $display: $t('message.timespan'),
    },
];

export const INTERVAL_REFERENCE_CHOICES = [
    {
        id: 'workday',
        $display: $t('message.tag_form_interval_reference_workday'),
    },
    {
        id: 'timespan',
        $display: $t('message.tag_form_interval_reference_timespan'),
    },
];

class ProjectService extends StorableBaseService<Project> {
    archivedFormFields: Array<FormField<any>>;

    getRequiredPermissions() { return ['projects:view']; }

    constructor() {
        super("/api/v2/projects/projects/");
        this.formFields = [
            new FormField("name", $t('message.name'), { autofocus: true }),
            new FormField("code", $t('message.code'), {}),
            new FormField("color", $t('message.color'), { type: 'color' }),
            new FormField<ProjectCategory>("category_id", $t('message.category'), {
                type: 'select',
                remoteSourceService: projectCategoryService,
                remoteSourceAttribute: "name",
            }),
            new FormField("group_assignments", $t('message.tag_form_group_assignments_label'), {
                type: "multiselect",
                description: $t('message.tag_form_group_assignments_description'),
                list: async (currentPage: number, pageSize: number, query: string) => {
                    return (await employeeGroupService.list(currentPage, pageSize, query)).data;
                },
                labelSingular: $t('message.department'),
                selectNoneLabel: $t('message.remove_selection'),
                allowNull: false,
                selectAllSetUndefined: false,
            }),
            new FormField("device_assignments", $t('message.tag_form_device_assignments_label'), {
                type: "multiselect",
                description: $t('message.tag_form_device_assignments_description'),
                list: async (currentPage: number, pageSize: number, query: string) => {
                    const deviceService = await getDeviceService();
                    const result = (await deviceService.list(currentPage, pageSize, query)).data;

                    // Add display attribute to each result
                    result.results.forEach((item: any) => {
                        item.$display = item.serial_number;
                        if (item.human_readable_name) item.$display += ' · ' + item.human_readable_name;
                    });

                    return result;
                },
                labelSingular: $t('message.device'),
                selectNoneLabel: $t('message.remove_selection'),
                allowNull: false,
                selectAllSetUndefined: false,
                displayAttribute: '$display',
            }),

            new FormField("locations", $t('message.tag_form_locations_label'), {
                type: 'radial-areas',
                description: $t('message.tag_form_locations_description'),
            }),
            new FormField("timebased_rules", $t('message.tag_form_timebased_rules_label'), {
                type: "weekly-timespans",
                description: $t('message.tag_form_timebased_rules_description'),
            }),
            new FormField("interval_based_rules", $t('message.tag_form_interval_based_rules_label'), {
                type: "interval-rules",
                description: $t('message.tag_form_interval_based_rules_description'),
            }),
            new FormField("value_type", $t('message.tag_form_value_type_label'), {
                type: "select",
                allowNull: false,
                description: $t('message.tag_form_value_type_description'),
                remoteSourceService: {
                    list: () => Promise.resolve({
                        data: {
                            results: TYPE_CHOICES,
                        }
                    }),
                } as any,
                remoteSourceAttribute: 'id',
                onChange: (formFields: Array<FormField<Project>>, resource: Project, oldResource?: Project) => {
                    if (!oldResource?.value_type) return;
                    if (resource.value_type == oldResource?.value_type) return;

                    resource.value = resource.value_type == 'relative' ? "100" : "0";
                },
                default: 'relative',
            }),
            new FormField("value", $t('message.share'), {
                type: "number",
                allowNull: false,
                description: $t('message.tag_form_value_description'),
                minimum: 0,
                maximum: 500,
                unit: 'percent',
                isVisible: (resource: Project) => resource.value_type == 'relative',
                default: 100,
            }),
            new FormField("value", $t('message.timespan'), {
                type: 'duration',
                description: $t('message.tag_form_value_description'),
                minimum: 0,
                maximum: 24 * 60 * 60,
                step: 60,
                isVisible: (resource: Project) => resource.value_type == 'absolute',
            }),
            new FormField("value_employee_configurable", $t('message.tag_form_value_employee_configurable_label'), {
                type: "bool",
                description: $t('message.tag_form_value_employee_configurable_description'),
            }),
        ];

        this.archivedFormFields = [
            new FormField("name", "Name", { isReadOnly: true }),
            new FormField("code", "Code", { isReadOnly: true }),
            new FormField("color", "Farbe", { type: 'color', isReadOnly: true }),
        ];

        this.cacheTimeout = 60;
    }

    newResourceTitle(resourceId?: string): Promise<string> { return Promise.resolve("Label hinzufügen") }
    newResourceConfirmation(resourceId?: string): Promise<string> { return Promise.resolve("Label hinzugefügt") }
    deleteResourceMethod(resourceId?: string): Promise<string> { return Promise.resolve("archive") }
    deleteResourceTitle(resourceId?: string): Promise<string> { return Promise.resolve("Archivieren") }
    deleteResourceConfirmation(resourceId?: string): Promise<string> { return Promise.resolve("Label archiviert") }

    listParams(params: ProjectListParams): Promise<AxiosResponse<PaginatedResponse<Project>>> {
        if (!this.hasPermissions()) return this.emptyResultsPromise();

        params = params || {};

        const pagesize = params.pagesize || 50;
        const page = params.page || 1;
        const query = params.query || '';

        let url =
            '?limit=' + String(pagesize) +
            '&offset=' + String((page - 1) * pagesize) +
            '&q=' + encodeURIComponent(String(query));

        if (params.ids && params.ids.length > 0) url += '&ids=' + params.ids.join("|");
        if ((params.ids && params.ids.length > 0) || params.includeArchived) url += '&archived=all';

        if (params.category_ids && params.category_ids.length > 0) url += '&category_ids=' + params.category_ids.join("|");

        if (params.recent) url += '&recent';
        if (params.forEmployee) url += '&for-employee=' + params.forEmployee;

        // Add results to details cache
        return this._get(url).then((response: AxiosResponse<PaginatedResponse<Project>>) => {
            if (!response) return response;
            this._cacheDetails(response.data.results);
            return response;
        })
    }

    archive(resourceId: string): Promise<AxiosResponse<Project>> {
        const promise = this._post(resourceId + "/archive/", {});

        // Clean cache after archive request was successfull
        promise.then(() => {
            this.clearCache();
        });

        return promise as unknown as Promise<AxiosResponse<Project>>;
    }

    unarchive(resourceId: string): Promise<AxiosResponse<Project>> {
        const promise = this._post(resourceId + "/unarchive/", {});

        // Clean cache after unarchive request was successfull
        promise.then(() => {
            this.clearCache();
        });

        return promise as unknown as Promise<AxiosResponse<Project>>;
    }
}

const projectService = new ProjectService();

export { projectService, ProjectService };
export type { Project, TagAssignment };

export default projectService;
